import { StoreCore, createStore } from "@priolo/jon"
import authApi from "api/auth"
import authPasswordApi from "api/authPassword"
import i18n from "i18next"
import { GetKeycloak } from "plugins/keycloak"
import dialogSo, { DIALOG_TYPE } from "stores/layout/dialogStore"
import { URL_PAR } from "stores/route/utils/types"
import tfaSo from "stores/user/tfa"
import { OsConfig, User } from "types/User"
import authService, { LOGIN_RESPONSE, getAppUrl } from "../../plugins/Auth"



const isPLC = import.meta.env.VITE_TARGET == "plc"
const isGPT = import.meta.env.VITE_TARGET == "gpt"
const isPlcAdmin = (new URLSearchParams(window.location.search)).get(URL_PAR.ROLE) == "admin" && isPLC

export enum LOGIN_TYPE {
	LEGACY = "legacy",
	KEYCLOAK = "keycloak",
	FEDERATION = "federation",
}

const setup = {

	state: {
		/** tipo di login */
		loginType: <LOGIN_TYPE>LOGIN_TYPE.LEGACY,
		/** the user is currently logged in */
		current: <User>null,
		/** selected user */
		select: <User>null,
		/** la configurazione del OS */
		config: <OsConfig>{
			sso: {
				enabled: !import.meta.env.VITE_KEYCLOAK_DISABLED || import.meta.env.VITE_KEYCLOAK_DISABLED == "true" ? false : true,
				url: import.meta.env.VITE_KEYCLOAK_URL,
				realm: import.meta.env.VITE_KEYCLOAK_REALM,
				clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
			},
			copilotkit: {
				enabled: !import.meta.env.VITE_COPILOTKIT_POPUP_DISABLED || import.meta.env.VITE_COPILOTKIT_POPUP_DISABLED == "true" ? false : true,
				proxy: {
					url: import.meta.env.VITE_COPILOTKIT_PROXY_URL,
				},
			},
		},

		//#region FORM
		email: "",
		password: "",
		//#endregion

		/** indicates whether the profile menu is open or not */
		menuOpen: false,
		repassword: "",

		// kiosk mode
		kioskKey: (new URLSearchParams(window.location.search)).get("kiosk_key"),
	},

	getters: {
		/** restituisce true se è stato fatto correttamente il login */
		isLogged: (_: void, store?: UserStore) => {
			return !!store.state.current
		},
		/** restituisce true se l'utente correntemente loggato è admin */
		isAdmin: (_: void, store?: UserStore) => {
			if (!!store.state.kioskKey) return false
			if (isPLC && store.state.loginType == LOGIN_TYPE.LEGACY) return isPlcAdmin
			return store.state.current?.role == "admin"
		},
		/**
		 * ATTENZIONE al momento non rileva il ruolo dello user ma è sempre true
		 */
		isReader: (_: void, store?: UserStore) => {
			if (!!store.state.kioskKey) return true
			// [II] al momento il controllo viene fatto da BE quindi lo do FALSE per permettere di editare la parte PLC
			return false
			//return store.state.current?.role == "reader"
		},
		/** restituisce true se l'utente correntemente loggato è un debugger */
		isDebugger: (_: void, store?: UserStore) => {
			return store.state.current?.debugEnabled ?? false
		},
		/** indica se l'utente ha accesso al PLC tramite NATS */
		plcAccess: (_: void, store?: UserStore) => {
			if (isPLC) return true
			return store.state.current?.hasPlcAccess ?? false
		},
		/**
		 * Non c'e' bisogno di AUTH se sono in 
		 * - GPT 
		 * - PLC e non è abilitato SSO
		 * - nella query string c'e' il paramentro 'kiosk_key' (per il kiosk mode)
		 */
		noAuth: (_: void, store?: UserStore) => {
			if (!!store.state.kioskKey) return true
			return (isPLC && !store.state.config?.sso?.enabled) || isGPT
		}
	},

	actions: {

		/** retrieves the currently logged in user */
		fetchLegacy: async (_: void, store?: UserStore) => {
			const auth = authService.get()
			// non c'e' nessun JWT!
			if (!auth) {
				store.setCurrent(null)
				return
			}
			return await store.fetchCurrent()
		},

		/** REST sul corrente user */
		fetchCurrent: async (_: void, store?: UserStore) => {
			let user: User
			try {
				const { data } = await authApi.getCurrent()
				user = data
				store.setCurrent(user)
			} catch (err) {
				store.logout()
			}
			return user
		},

		/**
		 * Recupera i dati di configurazione del OS, che includono le info per l'autenticazione via SSO (Keycloak)
		 */
		fetchOSConfig: async (_: void, store?: UserStore) => {
			const config = (await authApi.getOSConfig())?.data
			store.state.config = config
		},



		/**
		 * Login via Keycloak
		 */
		loginKeycloak: async (_: void, store?: UserStore) => {
			await GetKeycloak().login()
		},

		/** log in with the status values set "email" and "password" */
		loginLegacy: async (_: void, store?: UserStore) => {
			// recupero il token
			let response: any
			try {
				response = await authApi.login(
					store.state.email,
					store.state.password,
					tfaSo.state.otpCode
				)
				// se c'e' un errore controllo se c'e' bisogno di OTP
			} catch (error) {
				const otpRequiredForLogin = error.data?.otpRequiredForLogin
				if (otpRequiredForLogin) return LOGIN_RESPONSE.NEEDS_OTP
				dialogSo.dialogOpen({
					type: DIALOG_TYPE.ERROR,
					title: i18n.t("dialog.ajax.401.title"),
					text: i18n.t(`dialog.ajax.401.${error?.error?.includes("OTP") ? "otp-code" : "login"}.text`)
				})
				return LOGIN_RESPONSE.ERROR
			}
			// setto il token
			authService.set(response)
			// recupero l'utente corrente
			const user = await store.fetchLegacy()
			if (!user) return LOGIN_RESPONSE.ERROR
			store.resetSecret()
			// setto il tipo di login
			store.setLoginType(LOGIN_TYPE.LEGACY)
			// se era un login con otp-code allora lo setto ad abilitato
			if (tfaSo.state.otpCode) {
				tfaSo.setIs2FAEnabled(true)
				tfaSo.setIs2FAConfigured(true)
				// se l'otp non è configurato e l'utente lo deve abilitare...
			} else if (user.forceEnabledTwoFa) {
				return LOGIN_RESPONSE.FORCE_2FA
			}
			return LOGIN_RESPONSE.LOGGED
		},

		/** Log out */
		logout: async ({ noHttp, reload }: { noHttp?: boolean, reload?: boolean } = {}, store?: UserStore) => {
			store.setMenuOpen(false)
			if (!noHttp && store.isLogged()) {
				if (GetKeycloak()?.authenticated) {
					// Keycloak gestisce da solo la navigazione: redirige all'endpoint di
					// end-session (che cancella la sessione SSO) e poi torna a redirectUri.
					// NON facciamo il reload manuale: ricaricherebbe la SPA prima che la
					// sessione SSO sia stata terminata e check-sso ri-autenticherebbe lo
					// stesso utente (pagina aggiornata ma utente ancora loggato).
					authService.remove()
					await GetKeycloak().logout({ redirectUri: window.location.origin + getAppUrl() })
					return
				}
				await authApi.logout()
			}
			authService.remove()
			if (reload) {
				window.location.reload()
			} else {
				store.setCurrent(null)
			}
		},

		/** Retrieve a user using the recovery token */
		fetchUserWithRecoveryToken: async (token: string, store?: UserStore) => {
			store.setSelect(null)
			const { data: user } = await authPasswordApi.getUserByToken(token)
			store.setSelect(user)
		},

		/** send an email with instructions to change the password */
		sendRequestChangePassword: async (_: void, store?: UserStore) => {
			await authPasswordApi.getEmailInstructions(store.state.email)
		},

		/**
		 * change the password via a token
		 * {"password":{"password":"password","password_confirmation":"password", "reset_password_token":"c8RfivTxQ4yzK1kFdMLt"}}
		 */
		sendResetPassword: async (token: string, store?: UserStore) => {
			await authPasswordApi.resetPassword(store.state.password, store.state.repassword, token)
			store.resetSecret()
		},
	},

	mutators: {
		setLoginType: (loginType: LOGIN_TYPE) => ({ loginType }),

		setCurrent: (current: User) => ({ current }),
		setSelect: (select: User) => ({ select }),
		setMenuOpen: (menuOpen: boolean) => ({ menuOpen }),

		setEmail: (email: string) => ({ email }),
		setPassword: (password: string) => ({ password }),
		resetSecret: () => {
			tfaSo.setOtpCode("")
			tfaSo.setOtpSecret(null)
			tfaSo.setQRCode(null)
			return {
				password: "",
				repassword: "",
			}
		},
		setRePassword: (repassword: string) => ({ repassword }),
	},

}

export type UserState = typeof setup.state
export type UserGetters = typeof setup.getters
export type UserActions = typeof setup.actions
export type UserMutators = typeof setup.mutators
export interface UserStore extends StoreCore<UserState>, UserGetters, UserActions, UserMutators {
	state: UserState
}
const userSo = createStore(setup) as UserStore
export default userSo